import type { FilterExpression, SimpleFilter } from '../types/filter.types'

/**
 * Type guard to check if filter is already in expressive format
 * @param filter - Filter to check
 * @returns true if filter is FilterExpression (array of arrays)
 */
export function isExpressiveFilter(filter: unknown): filter is FilterExpression {
  return Array.isArray(filter) && filter.every(Array.isArray)
}

/**
 * Converts simple filter object to expressive format
 * Each key-value pair becomes a separate AND condition
 *
 * @example
 * buildExpressiveFilter({ activo: true, tipo: "A" })
 * // Returns: [[{ activo: true }], [{ tipo: "A" }]]
 *
 * @param simpleFilter - Simple filter object
 * @returns Expressive filter format
 */
export function buildExpressiveFilter(simpleFilter: SimpleFilter): FilterExpression {
  return Object.entries(simpleFilter).map(([key, value]) => [{ [key]: value }])
}

/**
 * Normalizes filter input to expressive format
 * Passes through expressive filters unchanged, converts simple filters
 *
 * @param filters - Simple or expressive filter (or undefined)
 * @returns Expressive filter format or undefined
 */
export function normalizeFilters(
  filters?: SimpleFilter | FilterExpression
): FilterExpression | undefined {
  if (!filters) return undefined
  if (isExpressiveFilter(filters)) return filters
  return buildExpressiveFilter(filters as SimpleFilter)
}

/**
 * Merges multiple filter expressions with AND logic
 *
 * @param filters - Array of filter expressions to merge
 * @returns Combined filter expression
 */
export function mergeFilters(...filters: (FilterExpression | undefined)[]): FilterExpression {
  return filters.filter(Boolean).flat() as FilterExpression
}
